import React, { useEffect, useState } from 'react';
import { ArticleIdentifier } from '../../../store/Models/Surveys/SurveyDataModel';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { DropdownMenu } from '../DropdownMenu';
import { LoadingSpinner, LoadingSpinnerSize } from '../LoadingSpinner';
import TextInput from '../TextInput';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

import "./styles.css";

interface ArticleIdentifierType {
    identifier: string,
    name: string
}

interface ArticleIdentifierProps {
    value?: ArticleIdentifier;
    onValueChange?: (value: ArticleIdentifier) => void;
    idPrefix: string;
    localizer: IStringLocalizer;
}

const ScipArticleIdentifier: React.FC<ArticleIdentifierProps> = ({ value, onValueChange, idPrefix, localizer }) => {
    const otherIdentifierTypeId = "1342";

    const [initialCategoriesLoaded, setInitialCategoriesLoaded] = useState(false);
    const [inputText, setInputValue] = useState(value?.value || '');
    const [typeOtherText, setTypeOtherText] = useState(value?.typeOtherText || '');
    const [articleIdentifierValue, setArticleIdentifierValue] = useState(value?.identifierTypeId && value.identifierTypeId.length > 0 ? value.identifierTypeId : otherIdentifierTypeId);
    const [articleIdentifiers, setArticleIdentifiers] = useState([] as ArticleIdentifierType[]);

    useEffect(() => {
        let doNotRender = false;
        const serviceClient = new ToxnotServiceClient();

        (async () => {
            const result = await serviceClient.scipIuclidStaticDataGet();

            if (!result.success || result.data === null) {
                throw new Error("Scip data was not valid");
            }

            const articles = [] as ArticleIdentifierType[];

            if (result.data.articleIdentifierTypeTable) {
                for (let article in result.data.articleIdentifierTypeTable) {
                    articles.push({
                        identifier: article,
                        name: localizer.localizeString(result.data.articleIdentifierTypeTable[Number(article)].trim())
                    });
                }
            }

            if (!doNotRender) {
                setArticleIdentifiers(articles);
                setInitialCategoriesLoaded(true);
            }
        })();

        return () => {
            doNotRender = true;
        };
    }, []);

    const geFriendlyNameOption = (option: string) => {
        return articleIdentifiers.first(a => a.identifier == option).name;
    }

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);

        if (onValueChange) {
            onValueChange({
                identifierTypeId: articleIdentifierValue,
                value: newValue,
                typeOtherText: typeOtherText
            });
        }
    }

    const handleTypeOtherTextChange = (newValue: string) => {
        setTypeOtherText(newValue);

        if (onValueChange) {
            onValueChange({
                identifierTypeId: articleIdentifierValue,
                value: inputText,
                typeOtherText: newValue
            });
        }
    }

    const handleDropdownChange = (newValue: string) => {
        setArticleIdentifierValue(newValue);

        if (onValueChange) {
            onValueChange({
                identifierTypeId: newValue,
                value: inputText,
                typeOtherText: typeOtherText
            });
        }
    }

    return (
        <div className="multi-text-parent clearfix">
            {
                !initialCategoriesLoaded && <LoadingSpinner size={LoadingSpinnerSize.Small} />
            }
            {initialCategoriesLoaded &&
                <div className="row">
                    <div className="col-xs-8 col-lg-5 col-xl-3">
                        <DropdownMenu
                            options={articleIdentifiers.map(a => a.identifier)}
                            getUniqueValueForOption={o => o}
                            getFriendlyNameForOptions={geFriendlyNameOption}
                            onOptionSelected={handleDropdownChange}
                            preselectedOption={articleIdentifierValue}
                            id={`${idPrefix}-dropdown`}
                        />
                        {articleIdentifierValue === otherIdentifierTypeId &&
                            <TextInput placeholder={localizer.localizeString("Type")} additionalClass="m-t-10" value={typeOtherText} onValueChange={handleTypeOtherTextChange} idPrefix={idPrefix}></TextInput>
                        }
                    </div>
                    <div className="col-xs-4 col-xl-3">
                        <TextInput value={inputText} onValueChange={handleInputChange} idPrefix={idPrefix}></TextInput>
                    </div>
                </div>
            }
        </div>
    );
};

export default ScipArticleIdentifier;
